import { useEffect, useRef, useState } from "react"
import { useLocation, useNavigate, useParams } from "react-router-dom"
import { api } from "../../../../env"
import Stepper from "./Stepper"
import BuildingBlocks from "./steps/BuildingBlocks"
import TrackDNA from "./steps/TrackDNA"
import ChaptersContent from "./steps/ChaptersContent"
import Review from "./steps/Review"
import { composeDescription } from "./helpers"
import "./BuildTrack.css"

export const STEPS = [
    { key: "blocks", label: "Building Blocks" },
    { key: "dna", label: "Track DNA" },
    { key: "chapters", label: "Chapters & Content" },
    { key: "review", label: "Review" },
]

const emptyBlocks = { name: "", audience: "", level: "", challenge: "", outcome: "", durationMonths: null }
const emptyDna = { coreBelief: "", capabilities: [], currentIdentity: "", futureIdentity: "", transformationStatement: "" }

// Track name/displayName must be >= 2 chars on the backend; fall back while the draft is incomplete.
const safeName = (blocks) => (blocks.name && blocks.name.trim().length >= 2 ? blocks.name : "Untitled track")
const draftMeta = (blocks, dna) => ({
    name: safeName(blocks),
    displayName: safeName(blocks),
    description: composeDescription(blocks, dna),
})

export default function BuildTrack() {

    const navigate = useNavigate()
    const location = useLocation()
    const { trackId: trackIdParam } = useParams()
    const isManagerContext = location.pathname.startsWith("/manager")
    const basePath = isManagerContext ? "/manager/tracks" : "/admin/tracks"

    const [step, setStep] = useState(0)
    const [blocks, setBlocks] = useState(emptyBlocks)
    const [dna, setDna] = useState(emptyDna)
    const [chapters, setChapters] = useState([])
    // True while the server is orchestrating a bulk generation for this draft. The server owns
    // buildState.chapters + buildState.generation for the duration, so autosave must stand down.
    const [genRunning, setGenRunning] = useState(false)

    // The backend Draft track this wizard is persisting to. Created when building blocks are done.
    const [trackId, setTrackId] = useState(null)
    const [hydrating, setHydrating] = useState(Boolean(trackIdParam))
    const createdRef = useRef(false)
    const saveTimer = useRef(null)

    // Only allow jumping to a step the user has already reached. Block navigation entirely while a
    // server generation run is live: the poller (which clears genRunning) lives on the Chapters step,
    // so leaving it would strand genRunning=true and freeze autosave on the other steps.
    const [maxStep, setMaxStep] = useState(0)
    const goTo = (target) => { if (genRunning) return; if (target <= maxStep) setStep(target) }
    const next = () => {
        const n = Math.min(step + 1, STEPS.length - 1)
        setStep(n)
        setMaxStep(m => Math.max(m, n))
    }
    const back = () => setStep(s => Math.max(s - 1, 0))

    // Resume an existing draft: load its saved wizard state. If it has already been finalized
    // (no buildState), send the user to the editor instead.
    useEffect(() => {
        if (!trackIdParam || createdRef.current) { setHydrating(false); return }
        let cancelled = false
        setHydrating(true)
        api.get(`tracks/${trackIdParam}`)
            .then(res => {
                if (cancelled) return
                const bs = res.data?.buildState
                if (!bs) {
                    navigate(isManagerContext ? `${basePath}/edit/${trackIdParam}` : `${basePath}/${trackIdParam}`, { replace: true })
                    return
                }
                if (bs.blocks) setBlocks(bs.blocks)
                if (bs.dna) setDna(bs.dna)
                // A chapter left mid-generation gets autosaved as a "loading" placeholder. If a
                // server run is still genuinely live (heartbeat fresh < 90s), keep the loading cards
                // and resume polling. Otherwise there's no live request behind it, so demote it to
                // "failed" — that card exposes the Retry/Delete affordances (a "loading" card has
                // neither, and Next blocks on it).
                if (Array.isArray(bs.chapters)) {
                    const gen = bs.generation
                    const fresh = gen?.updatedAt && (Date.now() - new Date(gen.updatedAt).getTime() < 90000)
                    if (gen?.status === "running" && fresh) {
                        setChapters(bs.chapters)
                        setGenRunning(true)
                    } else {
                        setChapters(bs.chapters.map(c => c.__status === "loading" ? { ...c, __status: "failed" } : c))
                    }
                }
                const s = Number.isInteger(bs.step) ? Math.min(bs.step, STEPS.length - 1) : 0
                setStep(s)
                setMaxStep(s)
                setTrackId(trackIdParam)
                setHydrating(false)
            })
            .catch(() => {
                // Draft not found / no access — start a fresh wizard.
                if (cancelled) return
                setHydrating(false)
                navigate(`${basePath}/build`, { replace: true })
            })
        return () => { cancelled = true }
    }, [trackIdParam]) // eslint-disable-line react-hooks/exhaustive-deps

    // Once a draft has had chapters, it should never be saved back empty. This latches true the
    // first time chapters are non-empty (hydration or generation) and guards the autosave below, so
    // a transient empty `chapters` (e.g. a remount mid-hydration) can never wipe the saved track.
    const hadChapters = useRef(false)
    useEffect(() => { if ((chapters?.length ?? 0) > 0) hadChapters.current = true }, [chapters])

    // Autosave the wizard state to the draft once it exists. Hard-bail while a server generation
    // run is live: the poller feeds server chapters into `chapters`, which would otherwise re-arm
    // this autosave and clobber the server's in-flight writes. Flipping genRunning true runs the
    // cleanup below, cancelling any armed timer. Once the run ends the next autosave PUTs buildState
    // WITHOUT a generation key, re-asserting client ownership and clearing the overlay.
    useEffect(() => {
        if (!trackId || genRunning) return
        // Never overwrite a track that has had chapters with an empty list (data-loss guard).
        if (hadChapters.current && (chapters?.length ?? 0) === 0) return
        if (saveTimer.current) clearTimeout(saveTimer.current)
        saveTimer.current = setTimeout(() => {
            api.put(`tracks/${trackId}`, { ...draftMeta(blocks, dna), buildState: { blocks, dna, chapters, step } })
                .catch(() => {}) // surfaced by the global error toast
        }, 800)
        return () => { if (saveTimer.current) clearTimeout(saveTimer.current) }
    }, [blocks, dna, chapters, step, trackId, genRunning])

    // Flush the current wizard state immediately (used just before kicking off a server generation,
    // so the server reads a consistent buildState). Returns the PUT promise.
    const flushSave = () => {
        if (!trackId) return Promise.resolve()
        if (saveTimer.current) clearTimeout(saveTimer.current)
        return api.put(`tracks/${trackId}`, { ...draftMeta(blocks, dna), buildState: { blocks, dna, chapters, step } })
            .catch(() => {}) // surfaced by the global error toast
    }

    // Completing the building blocks creates the Draft track, so work is never lost from here on.
    const startDraft = () => {
        if (trackId) { next(); return }
        api.post("tracks", { ...draftMeta(blocks, dna), buildState: { blocks, dna, chapters, step: 1 } })
            .then(res => {
                const id = res.data.id
                createdRef.current = true
                setTrackId(id)
                navigate(`${basePath}/build/${id}`, { replace: true })
                next()
            })
            .catch(() => {}) // surfaced by the global error toast; stay on the building-blocks step
    }

    const goToEditor = (id) => navigate(isManagerContext ? `${basePath}/edit/${id}` : `${basePath}/${id}`)

    if (hydrating) {
        return (<div className="standard-padding page-xl-centered showing">
            <p className="card-body neutral">Loading draft…</p>
        </div>)
    }

    return (<div className="standard-padding page-xl-centered showing">
        <div className="flex center gap-s v-spacer-m">
            <button className="icon outline large bt-back-btn" onClick={() => navigate("..")}>
                <span className="material-symbols-outlined">chevron_left</span>
            </button>
            <div className="flex columns gap-xxs">
                <p className="section-title">Build a Track</p>
                <p className="card-body neutral">Set the building blocks, shape the track, then let MentorMatches write the Activities and Sparks for every chapter.</p>
            </div>
        </div>

        <Stepper steps={STEPS} current={step} maxStep={maxStep} onSelect={goTo} />
        <div className="v-spacer-m" />

        {step === 0 && <BuildingBlocks value={blocks} onChange={setBlocks} onNext={startDraft} />}
        {step === 1 && <TrackDNA value={dna} onChange={setDna} blocks={blocks} onBack={back} onNext={next} />}
        {step === 2 && <ChaptersContent value={chapters} onChange={setChapters} blocks={blocks} dna={dna} trackId={trackId} genRunning={genRunning} setGenRunning={setGenRunning} flushSave={flushSave} onBack={back} onNext={next} />}
        {step === 3 && <Review blocks={blocks} dna={dna} chapters={chapters} trackId={trackId} isManagerContext={isManagerContext} onBack={back} onEditStep={goTo} onDone={goToEditor} />}
    </div>)
}
